import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type DailyLog, type Me } from './client'
import type { ActivityKind } from '../lib/schedule'
import type { Profile, WeightEntry } from '../lib/metrics'
import { todayIso } from '../lib/format'
import { toUpsert, type LastLog, type Session } from '../lib/session'
import type { Measurement } from '../lib/body'
import type { ActivityEntry } from '../lib/energy'

export const keys = {
  me: ['me'] as const,
  weights: ['weights'] as const,
  log: (date: string) => ['log', date] as const,
  logs: ['logs'] as const,
  schedule: ['schedule'] as const,
  sessions: ['sessions'] as const,
  measurements: ['measurements'] as const,
  activities: ['activities'] as const,
  lastLogs: ['lastLogs'] as const,
  usage: ['usage'] as const,
}

/** Yönetici kullanım özeti. Yalnızca Kullanıcılar sekmesi açıkken çekiliyor. */
export function useUsage() {
  return useQuery({ queryKey: keys.usage, queryFn: api.usage, retry: false })
}

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: api.me, retry: false })
}

export function useSaveProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: Profile) => api.saveProfile(p),
    // Optimistic: form anında tepki versin, ağ arkada halletsin.
    onMutate: async (p) => {
      await qc.cancelQueries({ queryKey: keys.me })
      const prev = qc.getQueryData<Me>(keys.me)
      if (prev) qc.setQueryData<Me>(keys.me, { ...prev, profile: p })
      return { prev }
    },
    onError: (_e, _p, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.me, ctx.prev)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.me }),
  })
}

export function useWeights() {
  return useQuery({ queryKey: keys.weights, queryFn: () => api.weights() })
}

export function useSaveWeight() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { date: string; kg: number; note?: string | null }) =>
      api.saveWeight(v.date, v.kg, v.note, new Date().toISOString()),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: keys.weights })
      const prev = qc.getQueryData<WeightEntry[]>(keys.weights) ?? []
      const next = [...prev.filter((w) => w.date !== v.date),
                    { date: v.date, kg: v.kg, note: v.note ?? null, updatedAt: new Date().toISOString() }]
        .sort((a, b) => (a.date < b.date ? -1 : 1))
      qc.setQueryData(keys.weights, next)
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.weights, ctx.prev)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.weights }),
  })
}

export function useDeleteWeight() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (date: string) => api.deleteWeight(date),
    onMutate: async (date) => {
      await qc.cancelQueries({ queryKey: keys.weights })
      const prev = qc.getQueryData<WeightEntry[]>(keys.weights) ?? []
      qc.setQueryData(keys.weights, prev.filter((w) => w.date !== date))
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.weights, ctx.prev)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.weights }),
  })
}

export function useTodayLog() {
  const date = todayIso()
  return useQuery({
    queryKey: keys.log(date),
    queryFn: async () => {
      const rows = await api.logs(date, date)
      return rows[0] ?? { date, checks: [], cycles: 0, standSeconds: 0, strengthDone: false, updatedAt: new Date().toISOString() }
    },
  })
}

export function useSaveLog() {
  const qc = useQueryClient()
  const date = todayIso()
  return useMutation({
    mutationFn: (v: Omit<DailyLog, 'date'>) => api.saveLog(date, v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: keys.log(date) })
      const prev = qc.getQueryData<DailyLog>(keys.log(date))
      qc.setQueryData<DailyLog>(keys.log(date), { date, ...v })
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.log(date), ctx.prev)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.log(date) }),
  })
}

// ── Antrenman kayıtları ─────────────────────────────────────────────────────

export function useSessions() {
  return useQuery({ queryKey: keys.sessions, queryFn: () => api.sessions() })
}

/** Her hareketin en son yapılışı — "geçen sefer" satırı ve ağırlık devri için. */
export function useLastLogs() {
  return useQuery({
    queryKey: keys.lastLogs,
    queryFn: async () => {
      const rows = await api.lastLogs()
      return Object.fromEntries(rows.map((r) => [r.log.slug, r])) as Record<string, LastLog>
    },
  })
}

export function useSaveSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (s: Session) => api.saveSession(s.date, toUpsert(s)),
    // Kaydedilen antrenman gün özetini de değiştiriyor (kuvvet işareti,
    // haftalık sayaç), o yüzden ilgili sorgular birlikte tazeleniyor.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: keys.sessions })
      qc.invalidateQueries({ queryKey: keys.lastLogs })
      qc.invalidateQueries({ queryKey: keys.schedule })
      qc.invalidateQueries({ queryKey: keys.logs })
      qc.invalidateQueries({ queryKey: keys.log(todayIso()) })
    },
  })
}

export function useDeleteSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (date: string) => api.deleteSession(date),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: keys.sessions })
      qc.invalidateQueries({ queryKey: keys.lastLogs })
      qc.invalidateQueries({ queryKey: keys.schedule })
      qc.invalidateQueries({ queryKey: keys.log(todayIso()) })
    },
  })
}

// ── Ölçüm ve aktivite ───────────────────────────────────────────────────────

export function useMeasurements() {
  return useQuery({ queryKey: keys.measurements, queryFn: api.measurements })
}

export function useSaveMeasurement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (m: Measurement) =>
      api.saveMeasurement(m.date, { ...m, updatedAt: new Date().toISOString() }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.measurements }),
  })
}

export function useDeleteMeasurement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (date: string) => api.deleteMeasurement(date),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.measurements }),
  })
}

export function useActivities() {
  return useQuery({ queryKey: keys.activities, queryFn: () => api.activities() })
}

export function useAddActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: ActivityEntry) => api.addActivity(a),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.activities }),
  })
}

export function useDeleteActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteActivity(id),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.activities }),
  })
}

export function useResetEnergy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.resetEnergy(),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.me }),
  })
}

export function useAdjustEnergy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (deltaKcal: number) => api.adjustEnergy(deltaKcal),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.me }),
  })
}

export function useSchedule() {
  return useQuery({ queryKey: keys.schedule, queryFn: api.schedule })
}

export function useSetOverride() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: {
      date: string; kind: ActivityKind; startTime: string | null
      activityKey?: string | null; minutes?: number | null
    }) => api.setOverride(v.date, v.kind, v.startTime,
      { activityKey: v.activityKey, minutes: v.minutes }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.schedule }),
  })
}

export function useClearOverride() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (date: string) => api.clearOverride(date),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.schedule }),
  })
}

export function useSaveTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (t: Parameters<typeof api.saveTemplate>[0]) => api.saveTemplate(t),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.schedule }),
  })
}
